import { lazy } from 'react';
import { SimpleGrid, Box, Text, Group, useMantineTheme, useComputedColorScheme, ThemeIcon, RingProgress } from '@mantine/core';
import { IconReceipt, IconClock, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { WidgetProps, InvoiceStats as InvoiceStatsType } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// InvoiceStats Widget Component
// ============================================================================

function InvoiceStatsWidget({ globalData, isCollapsed, settings }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Use computed text color if available (from container settings)
  const textColor = settings?.computedTextColor;
  const dimmedColor = textColor || 'dimmed';

  if (isCollapsed) return null;

  const stats = globalData?.invoiceStats || {
    total: 0,
    totalAmount: 0,
    pending: 0,
    pendingAmount: 0,
    paid: 0,
    paidAmount: 0,
    overdue: 0,
    overdueAmount: 0,
  };

  const statItems = [
    {
      label: 'Total Invoices',
      value: stats.total,
      amount: stats.totalAmount,
      icon: IconReceipt,
      color: 'blue',
    },
    {
      label: 'Pending',
      value: stats.pending,
      amount: stats.pendingAmount,
      icon: IconClock,
      color: 'yellow',
    },
    {
      label: 'Paid',
      value: stats.paid,
      amount: stats.paidAmount,
      icon: IconCheck,
      color: 'green',
    },
    {
      label: 'Overdue',
      value: stats.overdue,
      amount: stats.overdueAmount,
      icon: IconAlertTriangle,
      color: 'red',
    },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <FadeIn style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" style={{ flex: 1 }}>
        {statItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <Box
              key={item.label}
              p="sm"
              style={{
                backgroundColor: colorScheme === 'dark'
                  ? theme.colors[item.color][9]
                  : theme.colors[item.color][0],
                borderRadius: theme.radius.md,
              }}
            >
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color={item.color}>
                  <Icon size={14} />
                </ThemeIcon>
                <Text size="xs" c={dimmedColor} fw={500} style={accessibilityStyles}>
                  {item.label}
                </Text>
              </Group>
              <Text size="xl" fw={700} c={textColor || item.color} style={accessibilityStyles}>
                {item.value}
              </Text>
              <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                {formatCurrency(item.amount)}
              </Text>
            </Box>
          );
        })}
      </SimpleGrid>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'invoice-stats',
  name: 'Invoice Statistics',
  description: 'Overview of invoice metrics including total, pending, paid, and overdue',
  icon: IconReceipt,
  category: 'stats',
  component: lazy(() => Promise.resolve({ default: InvoiceStatsWidget })),
  sizes: {
    base: { cols: 12, rows: 3 },
    sm: { cols: 12, rows: 3 },
    md: { cols: 8, rows: 3 },
    lg: { cols: 6, rows: 3 },
    xl: { cols: 6, rows: 3 },
    tv: { cols: 6, rows: 3 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide', 'full'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 3 },
        sm: { cols: 6, rows: 3 },
        md: { cols: 4, rows: 3 },
        lg: { cols: 3, rows: 3 },
        xl: { cols: 3, rows: 3 },
        tv: { cols: 3, rows: 3 },
      },
      standard: {
        base: { cols: 12, rows: 3 },
        sm: { cols: 12, rows: 3 },
        md: { cols: 8, rows: 3 },
        lg: { cols: 6, rows: 3 },
        xl: { cols: 6, rows: 3 },
        tv: { cols: 6, rows: 3 },
      },
      wide: {
        base: { cols: 12, rows: 3 },
        sm: { cols: 12, rows: 3 },
        md: { cols: 12, rows: 3 },
        lg: { cols: 8, rows: 3 },
        xl: { cols: 8, rows: 3 },
        tv: { cols: 8, rows: 3 },
      },
      full: {
        base: { cols: 12, rows: 3 },
        sm: { cols: 12, rows: 3 },
        md: { cols: 12, rows: 3 },
        lg: { cols: 12, rows: 3 },
        xl: { cols: 12, rows: 3 },
        tv: { cols: 12, rows: 3 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['invoiceStats'],
  },
  hasSettings: true,
  expandable: true,
});

export default InvoiceStatsWidget;
